import React, {useState} from 'react';
import {Tabs} from '@mantine/core';
import {useSearchParams} from 'react-router-dom';
import type {CourseAssignmentLatenessBucketEnum} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkTrainingCompliance} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {TrainingLandingTabs} from 'src/pages/Modules/Training/shared/TrainingLandingTabs';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetCourseAssignmentAggregateQuery} from 'src/redux/queries/courseAssignment';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {TrainingComplianceTabView} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ComplianceAssignmentsGrid} from './components/ComplianceAssignmentsGrid';
import {ComplianceByCoursePivot} from './components/ComplianceByCoursePivot';
import {ComplianceByPersonPivot} from './components/ComplianceByPersonPivot';
import type {ComplianceFilters} from './components/ComplianceFiltersBar';
import {
  ComplianceFiltersBar,
  EMPTY_COMPLIANCE_FILTERS,
} from './components/ComplianceFiltersBar';
import {ComplianceNotAssignedPanel} from './components/ComplianceNotAssignedPanel';
import {ComplianceStatCards} from './components/ComplianceStatCards';

const COMPLIANCE_TABS: TrainingComplianceTabView[] = [
  'assignments',
  'by-person',
  'by-course',
  'not-assigned',
];

// Wave 1: read-only cockpit. Four StatCards drive a lateness-bucket filter
// on the assignments grid; two aggregate pivots (by person / by course)
// plus a "not assigned" view wired to the existing listPotentialCandidates
// endpoint. Active tab lives in the URL (`?tab=`) so detail pages opened
// from a pivot can return to the tab they were opened from.
const ComplianceDashboardPage: React.FC = () => {
  useSetDocumentTitle(TrainingDocumentTitle);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TrainingComplianceTabView = COMPLIANCE_TABS.includes(
    tabParam as TrainingComplianceTabView
  )
    ? (tabParam as TrainingComplianceTabView)
    : 'assignments';

  const [filters, setFilters] = useState<ComplianceFilters>(
    EMPTY_COMPLIANCE_FILTERS
  );
  const [latenessBucket, setLatenessBucket] = useState<
    CourseAssignmentLatenessBucketEnum | undefined
  >(undefined);

  const setActiveTab = (tab: TrainingComplianceTabView) => {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set('tab', tab);
        return next;
      },
      {replace: true}
    );
  };

  const handleSelectBucket = (bucket: CourseAssignmentLatenessBucketEnum) => {
    setLatenessBucket(current => (current === bucket ? undefined : bucket));
    setActiveTab('assignments');
  };

  const aggregateRequest = {
    courseDefinitionIds: filters.courseDefinitionIds.length
      ? filters.courseDefinitionIds.map(Number)
      : undefined,
    departmentId: filters.departmentId
      ? Number(filters.departmentId)
      : undefined,
    supervisorId: filters.supervisorId
      ? Number(filters.supervisorId)
      : undefined,
    vendorId: filters.vendorId ? Number(filters.vendorId) : undefined,
    projectId: filters.projectId ? Number(filters.projectId) : undefined,
    userTypes: filters.userTypes.length ? filters.userTypes : undefined,
    activeStates: [true],
  };
  const {
    data: aggregate,
    isLoading: isAggregateLoading,
    refetch: refetchAggregate,
  } = useGetCourseAssignmentAggregateQuery(aggregateRequest);

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkTrainingCompliance.icon}>
          Compliance
        </PageHeader.Title>
      </PageHeader>

      <TrainingLandingTabs active="compliance" />

      <ComplianceStatCards
        counts={aggregate?.statCards}
        activeBucket={latenessBucket}
        onSelectBucket={handleSelectBucket}
      />

      <ComplianceFiltersBar filters={filters} onChange={setFilters} />

      <Tabs
        value={activeTab}
        onChange={value =>
          setActiveTab((value as TrainingComplianceTabView) ?? 'assignments')
        }>
        <Tabs.List mb="md">
          <Tabs.Tab value="assignments">Assignments</Tabs.Tab>
          <Tabs.Tab value="by-person">By Person</Tabs.Tab>
          <Tabs.Tab value="by-course">By Course</Tabs.Tab>
          <Tabs.Tab value="not-assigned">Not Assigned</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="assignments">
          <ComplianceAssignmentsGrid
            filters={filters}
            latenessBucket={latenessBucket}
            onAggregateRefetch={refetchAggregate}
          />
        </Tabs.Panel>
        <Tabs.Panel value="by-person">
          <ComplianceByPersonPivot
            rows={aggregate?.byPerson ?? []}
            isLoading={isAggregateLoading}
          />
        </Tabs.Panel>
        <Tabs.Panel value="by-course">
          <ComplianceByCoursePivot
            rows={aggregate?.byCourse ?? []}
            isLoading={isAggregateLoading}
          />
        </Tabs.Panel>
        <Tabs.Panel value="not-assigned">
          <ComplianceNotAssignedPanel />
        </Tabs.Panel>
      </Tabs>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingCompliance(),
  element: <ComplianceDashboardPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default ComplianceDashboardPage;
